import { useTRPC } from '@/lib/trpc'
import { useMutation } from '@tanstack/react-query'
import { isJevComparisonModel } from '@repo/sdk'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { GameLog } from '@/components/play/GameLog'

import {
  GameNotificationSetupNotice,
} from '@/components/play/home-screen-notification-notice'
import { PlayerList } from '@/components/play/PlayerList'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useI18n } from '@/lib/i18n'
import {
  type ComparisonPrivateDetail,
  getComparisonPrivateDetailPresentation,
} from '@/lib/comparison-private-detail'
import { extractTopLevelResponseTags } from '@/lib/response-tags'
import { getNumericResponseLabel } from '@/lib/response-choice-label'
import { cn } from '@/lib/utils'
import { RoleIndexFloating } from '@/pages/role-index'
import { CheckCircle2, MessageSquare, XCircle } from 'lucide-react'
import { usePlayStatus } from '@/hooks/use-play-status'
import { useGameSeo } from '@/lib/game-seo'

function parseRawOutput(output: string) {
  const tags = extractTopLevelResponseTags(output)
  const extract = (tag: string) => tags.get(tag)?.trim() || null
  return {
    message: extract('x-response-message'),
    number: extract('x-response-number'),
    antidote: extract('x-response-antidote'),
    poison: extract('x-response-poison'),
    gargoyleKill: extract('x-response-gargoyle-kill'),
    magicianFirst: extract('x-response-magician-first'),
    magicianSecond: extract('x-response-magician-second'),
    luckySkill: extract('x-response-lucky-skill'),
    claimIdentity: extract('x-claim-identity'),
    direction: extract('x-response-direction'),
  }
}

interface ComparisonResponse {
  id: string
  roundId: string
  playerId: string
  actionType: string
  modelName: string
  speaking: string | null
  output: string
  thinkingDisplay: ComparisonPrivateDetail | null
  canChoose?: boolean
  isChosen: boolean | null
  actionId: string | null
  roundNumber: number
  phase: string
  subPhase: string
  createdAt: Date
}

function canChooseComparisonResponse(
  gameStatus: string | undefined,
  response: ComparisonResponse,
): boolean {
  return response.canChoose ?? gameStatus === 'paused'
}

export function ComparisonPlayPage({
  gameIdProp,
}: { gameIdProp?: string } = {}) {
  const trpc = useTRPC()
  const { t } = useI18n()
  const { gameId: paramGameId = '' } = useParams<{ gameId: string }>()
  const gameId = gameIdProp || paramGameId
  const isEmbedded = !!gameIdProp
  const [isRoleGuideOpen, setIsRoleGuideOpen] = useState(false)
  const [selectedComparisonIdx, setSelectedComparisonIdx] = useState<number | null>(null)

  const statusQuery = usePlayStatus(gameId)
  useGameSeo(!isEmbedded)

  useEffect(() => {
    statusQuery.markApplied(statusQuery.data?.statusVersion ?? null)
  }, [statusQuery])

  const { mutateAsync: chooseResponseAsync, isPending: isChoosing } = useMutation(
    trpc.playRouter.chooseComparisonResponse.mutationOptions(),
  )

  const comparisonGroups = useMemo(() => {
    const responses = (statusQuery.data?.comparisonResponses ?? []) as ComparisonResponse[]
    const groupsMap = new Map<string, ComparisonResponse[]>()

    for (const cr of responses) {
      const key = cr.actionId ?? `${cr.roundId}-${cr.playerId}-${cr.actionType}`
      if (!groupsMap.has(key)) {
        groupsMap.set(key, [])
      }
      groupsMap.get(key)!.push(cr)
    }
    return Array.from(groupsMap.values()).map(group =>
      group.sort((a, b) => a.id.localeCompare(b.id)),
    )
  }, [statusQuery.data?.comparisonResponses])

  const pendingGroupIdx = useMemo(() => {
    return comparisonGroups.findIndex(g => g.every(r => r.isChosen === null))
  }, [comparisonGroups])

  const activeGroupIdx = selectedComparisonIdx ?? (pendingGroupIdx >= 0 ? pendingGroupIdx : (comparisonGroups.length > 0 ? comparisonGroups.length - 1 : null))
  const activeGroup = activeGroupIdx !== null ? comparisonGroups[activeGroupIdx] : null
  const playersById = useMemo(
    () => new Map(
      statusQuery.data?.players.map(player => [player.id, player] as const) ?? [],
    ),
    [statusQuery.data?.players],
  )
  const comparisonSeatLabel = (playerId: string) => {
    const player = playersById.get(playerId)
    if (!player)
      return ''
    const seat = t('game.seat', { num: player.position })
    return player.name ? `${seat} · ${player.name}` : seat
  }

  const handleChoose = async (response: ComparisonResponse) => {
    if (
      !gameId
      || isChoosing
      || !canChooseComparisonResponse(statusQuery.data?.game?.status, response)
    ) {
      return
    }
    await chooseResponseAsync({
      gameId,
      comparisonResponseId: response.id,
    })
    setSelectedComparisonIdx(null)
    await statusQuery.refetch()
  }
  const isCompleted = statusQuery.data?.game?.status === 'completed'

  const combinedLog = useMemo(() => {
    const summary = statusQuery.data?.game?.summaryLog || ''
    const god = statusQuery.data?.game?.godLog || ''
    return [summary, god].filter(Boolean).join('\n\n')
  }, [statusQuery.data?.game?.summaryLog, statusQuery.data?.game?.godLog])

  const view = (
    <div
      className={cn(
        'flex w-full flex-col bg-background/95 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background',
        isEmbedded ? 'h-[80vh]' : 'h-screen',
      )}
      data-testid="comparison-page-frame"
    >
      <div className={cn(
        'mx-auto flex max-w-[95vw] flex-col gap-2 overflow-y-auto px-4 pb-4 lg:size-full lg:min-h-0 lg:overflow-hidden',
        isEmbedded ? 'pt-4' : 'pt-20',
      )}
      >
        {!isEmbedded && <GameNotificationSetupNotice />}

        <div className="flex flex-col gap-2 lg:min-h-0 lg:flex-1 lg:flex-row lg:gap-4">
          { }
          <div className="flex h-[40vh] min-h-0 flex-col gap-2 lg:h-auto lg:w-[340px] lg:flex-1 lg:flex-shrink-0">
            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]"
              data-testid="comparison-player-list-scroll"
              role="region"
              aria-label={t('play.playerList')}
              tabIndex={0}
            >
              <PlayerList data={statusQuery.data} showRoles />
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              {isCompleted ? (
                <GameLog
                  playerLog={statusQuery.data?.game?.summaryLog || ''}
                  godLog={statusQuery.data?.game?.godLog}
                  status={statusQuery.data}
                />
              ) : (
                <GameLog
                  playerLog={combinedLog}
                  godLog={combinedLog}
                  status={statusQuery.data}
                />
              )}
            </div>
          </div>

          { }
          <div className="flex min-h-0 flex-[2] flex-col gap-2">
            {activeGroup && (
              <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-2">
                {activeGroup.map((response) => {
                  const isPending = response.isChosen === null
                  const isChosen = response.isChosen === true
                  const isRejected = response.isChosen === false
                  const canChoose = canChooseComparisonResponse(
                    statusQuery.data?.game?.status,
                    response,
                  )

                  return (
                    <Card
                      key={response.id}
                      className={cn(
                        'flex flex-col overflow-hidden transition-all',
                        isChosen && 'border-green-500/50 shadow-lg shadow-green-500/10',
                        isRejected && 'border-red-500/30 opacity-70',
                        isPending && 'border-amber-400/30',
                      )}
                    >
                      <CardHeader className="flex-none space-y-0 pb-2">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <CardTitle className="break-words text-sm font-medium">
                              {isJevComparisonModel(response.modelName)
                                ? 'Jev'
                                : response.modelName.split('/').pop()}
                            </CardTitle>
                            <p className="mt-1 break-words text-xs text-muted-foreground">
                              {comparisonSeatLabel(response.playerId)}
                            </p>
                          </div>
                          {isChosen && (
                            <Badge className="gap-1 bg-green-500/15 text-green-600 dark:text-green-400">
                              <CheckCircle2 className="size-3" />
                              {t('play.comparison.chosen')}
                            </Badge>
                          )}
                          {isRejected && (
                            <Badge variant="secondary" className="gap-1 text-red-500">
                              <XCircle className="size-3" />
                              {t('play.comparison.rejected')}
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto text-sm">
                        {(() => {
                          const parsed = parseRawOutput(response.output)
                          const privateDetail
                            = getComparisonPrivateDetailPresentation(
                              response.thinkingDisplay,
                            )
                          return (
                            <>
                              { }
                              {parsed.message && (
                                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                                  <div className="mb-1 flex items-center gap-1 text-xs font-medium text-primary/80">
                                    <MessageSquare className="size-3" />
                                    {t('play.comparison.speaking')}
                                  </div>
                                  <p className="whitespace-pre-wrap text-sm leading-relaxed">
                                    {parsed.message}
                                  </p>
                                </div>
                              )}
                              { }
                              {(parsed.number || parsed.antidote || parsed.poison || parsed.gargoyleKill || parsed.magicianFirst || parsed.magicianSecond || parsed.claimIdentity || parsed.luckySkill || parsed.direction) && (
                                <div className="grid grid-cols-2 gap-2">
                                  {parsed.number && (
                                    <div className="rounded-md border border-orange-400/30 bg-orange-50/50 px-2.5 py-1.5 dark:bg-orange-950/20">
                                      <span className="text-[10px] font-medium uppercase text-orange-600 dark:text-orange-400">
                                        {t(`actions.${response.actionType}`)}
                                      </span>
                                      <p className="text-sm font-semibold">
                                        {getNumericResponseLabel(parsed.number, response.actionType, t)}
                                      </p>
                                    </div>
                                  )}
                                  {parsed.antidote && (
                                    <div className="rounded-md border border-emerald-400/30 bg-emerald-50/50 px-2.5 py-1.5 dark:bg-emerald-950/20">
                                      <span className="text-[10px] font-medium uppercase text-emerald-600 dark:text-emerald-400">💊 Antidote</span>
                                      <p className="text-sm font-semibold">{parsed.antidote === '-1' ? 'Not used' : `Player #${parsed.antidote}`}</p>
                                    </div>
                                  )}
                                  {parsed.poison && (
                                    <div className="rounded-md border border-violet-400/30 bg-violet-50/50 px-2.5 py-1.5 dark:bg-violet-950/20">
                                      <span className="text-[10px] font-medium uppercase text-violet-600 dark:text-violet-400">☠️ Poison</span>
                                      <p className="text-sm font-semibold">{parsed.poison === '-1' ? 'Not used' : `Player #${parsed.poison}`}</p>
                                    </div>
                                  )}
                                  {parsed.gargoyleKill && (
                                    <div className="rounded-md border border-red-400/30 bg-red-50/50 px-2.5 py-1.5 dark:bg-red-950/20">
                                      <span className="text-[10px] font-medium uppercase text-red-600 dark:text-red-400">🗡️ Gargoyle Kill</span>
                                      <p className="text-sm font-semibold">{parsed.gargoyleKill === '-1' ? 'None' : `Player #${parsed.gargoyleKill}`}</p>
                                    </div>
                                  )}
                                  {parsed.magicianFirst && (
                                    <div className="rounded-md border border-fuchsia-400/30 bg-fuchsia-50/50 px-2.5 py-1.5 dark:bg-fuchsia-950/20">
                                      <span className="text-[10px] font-medium uppercase text-fuchsia-600 dark:text-fuchsia-400">🪄 {t('play.comparison.magicianFirst')}</span>
                                      <p className="text-sm font-semibold">
                                        {parsed.magicianFirst === '-1'
                                          ? t('play.comparison.none')
                                          : t('game.seat', {
                                              num: parsed.magicianFirst,
                                            })}
                                      </p>
                                    </div>
                                  )}
                                  {parsed.magicianSecond && (
                                    <div className="rounded-md border border-fuchsia-400/30 bg-fuchsia-50/50 px-2.5 py-1.5 dark:bg-fuchsia-950/20">
                                      <span className="text-[10px] font-medium uppercase text-fuchsia-600 dark:text-fuchsia-400">🪄 {t('play.comparison.magicianSecond')}</span>
                                      <p className="text-sm font-semibold">
                                        {parsed.magicianSecond === '-1'
                                          ? t('play.comparison.none')
                                          : t('game.seat', {
                                              num: parsed.magicianSecond,
                                            })}
                                      </p>
                                    </div>
                                  )}
                                  {parsed.claimIdentity && (
                                    <div className="rounded-md border border-sky-400/30 bg-sky-50/50 px-2.5 py-1.5 dark:bg-sky-950/20">
                                      <span className="text-[10px] font-medium uppercase text-sky-600 dark:text-sky-400">🎭 Claimed Identity</span>
                                      <p className="text-sm font-semibold">{parsed.claimIdentity}</p>
                                    </div>
                                  )}
                                  {parsed.luckySkill && (
                                    <div className="rounded-md border border-amber-400/30 bg-amber-50/50 px-2.5 py-1.5 dark:bg-amber-950/20">
                                      <span className="text-[10px] font-medium uppercase text-amber-600 dark:text-amber-400">🍀 Lucky Skill</span>
                                      <p className="text-sm font-semibold">{parsed.luckySkill}</p>
                                    </div>
                                  )}
                                  {parsed.direction && (
                                    <div className="rounded-md border border-blue-400/30 bg-blue-50/50 px-2.5 py-1.5 dark:bg-blue-950/20">
                                      <span className="text-[10px] font-medium uppercase text-blue-600 dark:text-blue-400">🧭 Direction</span>
                                      <p className="text-sm font-semibold">{parsed.direction}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                              { }
                              {privateDetail && (
                                <div
                                  className="rounded-lg border border-border/30 bg-muted/20 p-2.5"
                                  data-testid={`comparison-private-detail-${privateDetail.kind}`}
                                >
                                  <div className="mb-1 text-[10px] font-medium uppercase text-muted-foreground">
                                    {privateDetail.icon}{' '}
                                    {t(privateDetail.labelKey)}
                                  </div>
                                  <p className="whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
                                    {privateDetail.value}
                                  </p>
                                </div>
                              )}
                            </>
                          )
                        })()}
                      </CardContent>
                      { }
                      {isPending && (
                        <div className="flex-none border-t border-border/50 p-3">
                          <Button
                            onClick={() => handleChoose(response)}
                            disabled={isChoosing || !canChoose}
                            className="w-full"
                            variant="outline"
                          >
                            {isChoosing ? '...' : t('play.comparison.choose')}
                          </Button>
                        </div>
                      )}
                    </Card>
                  )
                })}
              </div>
            )}

            { }
            {!activeGroup && statusQuery.data?.game.status !== 'completed' && (
              <div className="flex flex-1 items-center justify-center text-muted-foreground">
                <p className="text-sm">
                  {t('play.comparison.waitingForNextComparison')}
                </p>
              </div>
            )}
          </div>

          { }
          <div className="flex flex-col gap-1 lg:w-48 lg:flex-shrink-0 lg:overflow-y-auto">
            <h3 className="mb-1 text-sm font-semibold text-muted-foreground">
              {t('play.comparison.history')}
            </h3>
            {comparisonGroups.map((group, idx) => {
              const isPending = group.every(r => r.isChosen === null)
              const isActive = idx === activeGroupIdx
              return (
                <button
                  type="button"
                  key={group[0].id}
                  data-testid={`comparison-history-${group[0].actionId ?? group[0].id}`}
                  onClick={() => setSelectedComparisonIdx(idx)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all',
                    isActive ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card hover:bg-muted/50',
                    !isPending && !isActive && 'opacity-60',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <p className={cn(
                      'text-sm font-medium leading-none',
                      isActive ? 'text-foreground' : 'text-muted-foreground',
                    )}
                    >
                      {t(`actions.${group[0].actionType}` as any)}
                    </p>
                    <p className="mt-1 break-words text-xs text-muted-foreground/80">
                      {t('play.comparison.round', { round: group[0].roundNumber })}
                      {' · '}
                      {comparisonSeatLabel(group[0].playerId)}
                    </p>
                  </div>
                  {isPending ? (
                    <div className="size-2 animate-pulse rounded-full bg-amber-400" />
                  ) : (
                    <CheckCircle2 className="size-3 text-green-500" />
                  )}
                </button>
              )
            })}
            {comparisonGroups.length === 0 && (
              <p className="text-xs text-muted-foreground">
                {statusQuery.data?.game.status === 'in_progress'
                  ? t('play.comparison.waitingForNextComparison')
                  : 'No comparisons yet'}
              </p>
            )}
          </div>
        </div>
      </div>
      {!isEmbedded && (
        <RoleIndexFloating
          showButton={false}
          isOpen={isRoleGuideOpen}
          onClose={() => setIsRoleGuideOpen(false)}
        />
      )}
    </div>
  )
  return view
}
